
'use client'

import { useState, useEffect } from "react";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import styled from "styled-components";
import { DateTime } from "luxon";

import md from 'markdown-it';
import attrs from "markdown-it-attrs";
import highlight from "markdown-it-mark";

import { SOCIAL } from "@/utils/variables";
import Post from "@/components/Post";
import { PostType } from "@/components/Post/Post";

export type PostProps = PostType & {
  content: string;
}

const PostMain = styled.div`
  .banner-container {
    width: 100%;
    height: 20rem;
    position: relative;

    @media ${({ theme }) => theme.mediaQuery.phone} {
      height: 25rem;
    }

    @media ${({ theme }) => theme.mediaQuery.tablet} {
      height: 30rem;
    }

    @media ${({ theme }) => theme.mediaQuery.desktopLarge} {
      height: 40rem;
    }

    @media ${({ theme }) => theme.mediaQuery.desktopFHD} {
      height: 50rem;
    }

    img {
      object-fit: cover;
      object-position: center center;
      margin: 0;
    }
  }

  .content {
    width: 100%;
    margin: 0 auto;
    padding: 4rem 0;
    position: relative;

    .pre-content-container {
      margin: 0.25rem auto 0.5rem;
      max-width: 45rem;
      width: 100%;
      padding: 0 1rem;
      position: relative;

      .subtitle {
        color: #999;
        font-size: 0.9rem;
        margin-bottom: 0;
        font-weight: normal;
      }

      .back-button {
        background: rgba(0, 0, 0, 0.1);
        border-radius: 10rem;
        height: 4rem;
        width: 4rem;
        display: flex;
        align-items: center;
        justify-content: center;
        text-decoration: none;
        position: absolute;
        top: 2.5rem;
        left: -4rem;

        i {
          color: #333;
          margin-bottom: -0.2rem;
        }

        .text {
          position: absolute;
          bottom: 0;
          opacity: 0;
          font-size: 0.6rem;
          text-transform: uppercase;
          transform: translateY(0.5rem);
          transition: all 0.1s ease;
          color: #333;
        }

        &:hover {
          background: rgba(0, 0, 0, 0.15);

          .text {
            transform: translateY(1.25rem);
            opacity: 1;
          }
        }
      }
    }
  }
`

const PostReadMore = styled.div`

  width: 100%;
  max-width: 60rem;
  margin: 4rem auto;
  padding: 0 1rem;

  h4 {
    text-align: center; 
    margin-bottom: 1rem;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    grid-gap: 1.75rem 1rem;

    @media ${({ theme }) => theme.mediaQuery.phone} {
      grid-template-columns: repeat(2, 1fr);
    }
    
    & > :nth-child(n+2) {
      display: none;

      @media ${({ theme }) => theme.mediaQuery.phone} {
        display: block;
      }
    }
  }
`

const SocialContainer = styled.div`
  padding: 0rem 1rem 4rem;
  text-align: center;
  
  h4 {
    font-family: ${({ theme }) => theme.fonts.secondary};
  }

  @media ${({ theme }) => theme.mediaQuery.phone} {
    padding: 0rem 1rem 4rem;
  }
  
  .social {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 1rem;
    
    a {
      padding: 0.2rem;
      margin: 0 0.2rem;
      font-size: 1.6rem;
      color: black;
      opacity: 0.4;

      &:hover {
        opacity: 1;
      }
    }
  }
`

// The page for each post
const Page = ({ post, readMorePosts }: {post: PostProps, readMorePosts: PostProps[]}) => {
  const { frontmatter, content, slug } = post;

  const {title, description, author, date, bannerImage, tags} = frontmatter

  const markdown = md()
    .use(attrs as any)
    .use(highlight);

  const [origin, setOrigin] = useState(process.env.NEXT_PUBLIC_HOST || "https://plaen-rest.netlify.app")
  
  useEffect(() => {
    setOrigin(window.location.origin);
  }, []);

  return (
    <>
      <PostMain className="prose">
        <Head>
          <title>{title}</title>
          <meta name="keywords" content={tags.join()} />
          <meta name="description" content={description} />
          <meta name="author" content={author} />

          <meta property="og:type" content="website" />
          <meta property="og:url" content={`${origin}/posts/${slug}`} />
          <meta property="og:title" content={`Intersect Blog –⁠ ${title}`} />
          <meta property="og:description" content={description} />
          <meta property="og:image" content={`${origin}${bannerImage}`} />

          <meta property="twitter:card" content="summary_tablet_image" />
          <meta property="twitter:url" content={`${origin}/posts/${slug}`} />
          <meta property="twitter:title" content={`Intersect Blog –⁠ ${title}`} />
          <meta property="twitter:description" content={description} />
          <meta property="twitter:image" content={`${origin}${bannerImage}`} />
        </Head>
        <div className="banner-container">
          <Image src={bannerImage} fill alt={title} />
        </div>
        <div className="content">
          <div className="pre-content-container">
            <Link href="/blog" className="back-button">
              <i aria-hidden className="fa-solid fa-arrow-left" />
              <div className="text">Back</div>
            </Link>
            <p className="subtitle">{DateTime.fromISO(date).toFormat("LLL dd, yyyy")}</p>
          </div>
          <div className="inner" dangerouslySetInnerHTML={{ __html: markdown.render(content) }} />
        </div>
      </PostMain>
      {
        readMorePosts.length > 0 ? (
          <PostReadMore>
            <h4>Read More</h4>
            <div className="grid">
              { readMorePosts.map(item => (
                <Post post={item} key={item.slug} />
              ))}
            </div>
          </PostReadMore>
        ) : null
      }
      <SocialContainer>
        <h4>Want more? Follow us!</h4>
        <div className="social">
          {SOCIAL.map(({url, icon}, index) => (
            <Link key={index} href={url}>
              <i aria-hidden className={`fab ${icon}`} />
            </Link>
          ))}
        </div>
      </SocialContainer>
    </>
  )
}

export default Page;